import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { MinMaxNumber } from '../../inputs/MinMaxNumber'
import styles from '../../search.module.scss'
import { ComponentErrorBoundary } from '@/components/atomic/ComponentErrorBoundary'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import { Range } from '@/components/search/inputs/Range'
import { FILTER_MONTHLY_PAYMENT, FILTER_MONTHLY_PAYMENT_MAX_DEFAULT } from '@/lib/constants'
import { filterLabelPrettyName, removeVatFromPrice, formatWithCommas } from '@/lib/utilities/search'
import {
  selectActiveMonthlyMin,
  selectActiveMonthlyMax,
  selectAvailableMonthlyMax,
  selectAvailableMonthlyMin,
  selectIncludeVat,
} from '@/store/selectors/search'

type MonthlyPriceProps = {
  filter: string
  isFilterOpen: boolean
  toggleFilter: () => void
  onChange: (params: {
    type: 'multi' | 'single' | 'vehiclePriceTag'
    filter: string
    value: string | number
  }) => void
  isMobile: boolean
}

export const MonthlyPrice = ({
  filter,
  isFilterOpen,
  toggleFilter,
  onChange,
  isMobile = true,
}: MonthlyPriceProps) => {
  const availableMonthlyMin = useSelector(selectAvailableMonthlyMin)
  const availableMonthlyMax = useSelector(selectAvailableMonthlyMax)
  const activeMonthlyMin = useSelector(selectActiveMonthlyMin)
  const activeMonthlyMax = useSelector(selectActiveMonthlyMax)
  const includesVat = useSelector(selectIncludeVat)

  const [minVal, setMinVal] = useState(availableMonthlyMin)
  const [maxVal, setMaxVal] = useState(
    availableMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
      ? FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
      : availableMonthlyMax,
  )
  const [minInput, setMinInput] = useState(
    activeMonthlyMin && activeMonthlyMin >= availableMonthlyMin
      ? activeMonthlyMin
      : availableMonthlyMin,
  )
  const [maxInput, setMaxInput] = useState(
    availableMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
      ? FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
      : availableMonthlyMax,
  )
  const minGap = 50
  const minGapLarge = 250

  useEffect(() => {
    if (activeMonthlyMin && activeMonthlyMin >= availableMonthlyMin) {
      const value = activeMonthlyMin
      setMinInput(Math.floor(value))
      setMinVal(Math.floor(value))
    }

    if (!activeMonthlyMin) {
      const value = availableMonthlyMin
        setMinInput(Math.floor(value))
        setMinVal(Math.floor(value))
    }

    // For when the available monthly min is greater than the the active monthly max
    if (activeMonthlyMax && availableMonthlyMin >= activeMonthlyMax && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) {
      const value = activeMonthlyMin

      setMinInput(Math.floor(value))
      setMinVal(Math.floor(value))
    }
  }, [activeMonthlyMin, availableMonthlyMin, activeMonthlyMax])

  useEffect(() => {
    if (activeMonthlyMax && activeMonthlyMax <= availableMonthlyMax && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) {
      const value = activeMonthlyMax

      setMaxInput(value)
      setMaxVal(value)
    }

    if (activeMonthlyMax && activeMonthlyMax > availableMonthlyMax && !activeMonthlyMin && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) {
      const value = FILTER_MONTHLY_PAYMENT_MAX_DEFAULT

      setMaxInput(value)
      setMaxVal(value)
    }

    // For when the available monthly min is greater than the the default monthly max
    if (availableMonthlyMin >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && activeMonthlyMax === FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) {
      const value = availableMonthlyMax

      setMaxInput(Math.floor(value))
      setMaxVal(Math.floor(value))
    }

    // if active max is redux default
    if (activeMonthlyMax === 9999999) {
      let value = FILTER_MONTHLY_PAYMENT_MAX_DEFAULT

      if (availableMonthlyMin >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) {
        value = availableMonthlyMax
      }

      setMaxInput(Math.floor(value))
      setMaxVal(Math.floor(value))
    }
  }, [activeMonthlyMin, availableMonthlyMax, availableMonthlyMin, activeMonthlyMax])

  const onSearch = (newValue?: number, type?: 'min' | 'max') => {
    const maxValueToUse = type === 'max' ? newValue : maxVal
    const minValueToUse = type === 'min' ? newValue : minVal

    const amendedValueMax =
      maxValueToUse === FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
        ? 9999999
        : includesVat
          ? removeVatFromPrice(maxValueToUse)
          : maxValueToUse
    const amendedValueMin = includesVat ? removeVatFromPrice(minValueToUse) : minValueToUse

    if (onChange) {
      if (type === 'min') {
        onChange({
          type: 'single',
          filter: 'monthlyPaymentMin',
          value: amendedValueMin,
        })
      } else {
        onChange({
          type: 'single',
          filter: 'monthlyPaymentMax',
          value: amendedValueMax,
        })
      }
    }
  }

  const onMonthlyFinalChange = (changedValues: (number | string)[]) => {
    slideMin(Number(changedValues[0]))
    onSearch(Number(changedValues[0]), 'min')

    slideMax(Number(changedValues[1]))
    onSearch(Number(changedValues[1]), 'max')
  }

  const onMonthlyChange = (changedValues: (number | string)[]) => {
    slideMin(Number(changedValues[0]))
    slideMax(Number(changedValues[1]))
  }

  const slideMin = (value: number) => {
    if (value >= availableMonthlyMin && maxVal - value >= minGap) {
      setMinVal(value)
      setMinInput(Math.floor(value))
    }
  }

  const slideMax = (value: number) => {
    if (value <= availableMonthlyMax && value - minVal >= minGap) {
      setMaxVal(value)
      setMaxInput(Math.floor(value))
    }
  }

  const handleMinInput = (value: number) => {
    const newValue = Number(value) || 0

    if (newValue >= availableMonthlyMin && newValue < maxVal - minGap) {
      setMinVal(newValue)
      onSearch(newValue, 'min')
    }
    setMinInput(newValue.toFixed(0))
  }

  const handleMaxInput = (value: number) => {
    const newValue = Number(value) || 0

    if (newValue <= availableMonthlyMax && newValue > minVal + minGap) {
      setMaxVal(newValue)
      onSearch(newValue, 'max')
    }
    setMaxInput(newValue.toFixed(0))
  }

  return (
    <FilterDropdown
      title="Monthly price"
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={minVal !== availableMonthlyMin ||
        (availableMonthlyMin > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT ? maxVal !== availableMonthlyMax : maxVal !== FILTER_MONTHLY_PAYMENT_MAX_DEFAULT)
      }
      maximumNumberOfVisibleSelectedOptions={1}
      selectedOptions={[
        `${minVal ? filterLabelPrettyName(filter, minVal) : '£0'}-${maxVal >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && !(activeMonthlyMin > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT || activeMonthlyMax > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) || maxVal === FILTER_MONTHLY_PAYMENT_MAX_DEFAULT ? `£${formatWithCommas(FILTER_MONTHLY_PAYMENT_MAX_DEFAULT)}+` : filterLabelPrettyName(filter, maxVal)}`,
      ]}>
      <div className={styles.Budget}>
        <ComponentErrorBoundary>
          <Range
            filter={FILTER_MONTHLY_PAYMENT}
            filteredTypeName={FILTER_MONTHLY_PAYMENT}
            availableMinimum={
              availableMonthlyMin >= activeMonthlyMax || activeMonthlyMin < availableMonthlyMin ? activeMonthlyMin : availableMonthlyMin
            }
            availableMaximum={
              availableMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && activeMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && (activeMonthlyMax <= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT || activeMonthlyMax === 9999999)
                ? FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
                : availableMonthlyMax
            }
            activeMinimum={minVal}
            activeMaximum={maxVal}
            step={availableMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && !(activeMonthlyMin > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && activeMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT) ? isMobile ? minGap * 0.25 : minGap : isMobile ? minGapLarge * 0.25 : minGapLarge}
            onChange={onMonthlyChange}
            onFinalChange={onMonthlyFinalChange}
            isLoading={false}
            showValues={false}
          />
        </ComponentErrorBoundary>
        <MinMaxNumber
          activeMin={minVal}
          activeMax={maxVal}
          handleMinInput={handleMinInput}
          handleMaxInput={handleMaxInput}
          minInput={minInput}
          maxInput={maxInput}
          availableMaximum={
            availableMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && availableMonthlyMin < FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && !(activeMonthlyMin > FILTER_MONTHLY_PAYMENT_MAX_DEFAULT && activeMonthlyMax >= FILTER_MONTHLY_PAYMENT_MAX_DEFAULT)
              ? FILTER_MONTHLY_PAYMENT_MAX_DEFAULT
              : availableMonthlyMax
          }
          availableMinimum={availableMonthlyMin >= activeMonthlyMax ? activeMonthlyMin : availableMonthlyMin}
          minGap={minGap}
          label={FILTER_MONTHLY_PAYMENT}
        />
      </div>
    </FilterDropdown>
  )
}
